








































import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import FaceDirectionTracker, { TrackerErrorEventArgs } from '@/components/facetracker/FaceDirectionTracker.vue';
import FaceDirectionArrow from '@/components/facetracker/FaceDirectionArrow.vue';
import FaceDirectionGuide, { StatusRingGauge } from '@/components/facetracker/FaceDirectionGuide.vue';
import FaceDirectionResultEffect, { StatusResultEffect } from '@/components/facetracker/FaceDirectionResultEffect.vue';
import FaceDirectionMessage, { MessageType } from '@/components/facetracker/FaceDirectionMessage.vue';
import FaceDirectionModal, { MessageType as ErrorMessageType } from '@/components/facetracker/FaceDirectionModal.vue';
import StepProgress from '@/components/facetracker/StepProgress.vue';
import {
  Direction,
  DirectionDetectionPhase,
  TrackerStatus,
  TrackerType,
  Rotation,
  ErrorType
} from '@/services/facetracker';
import { setTimeoutAsync } from '@/services/utils';
import { CheckDirectionStatus } from '@/api/facetracker/FaceTracker';
import { ScreenOrientation } from '@/resources/enum/ScreenOrientation';
import { OsType, getOSType } from '@/util/device';

// Minimum API calling interval in milliseconds
const MINIMUM_INTERVAL = 200; // 5fps

enum ViewState {
  None,
  Success,
  Error
}

@Component({
  components: {
    FaceDirectionTracker,
    FaceDirectionArrow,
    FaceDirectionGuide,
    FaceDirectionResultEffect,
    FaceDirectionMessage,
    FaceDirectionModal,
    StepProgress
  }
})
export default class FaceDirection extends Vue {
  @Prop({ default: TrackerType.Server })
  trackerType!: TrackerType;

  $refs!: {
    tracker: FaceDirectionTracker;
  };

  messageType: MessageType = MessageType.None;
  lookLevel: number = 0;
  isLoading: boolean = true;
  showLiveArrow: boolean = false;
  viewState: ViewState = ViewState.None;
  errorMessageType: ErrorMessageType = ErrorMessageType.None;
  isManyRetry: boolean = false;
  guideStatusResultEffect: StatusResultEffect = 'none';
  guideStatusRingGauge: StatusRingGauge = 'none';
  guideProgress: number = 0;

  get installationOperatorName(): string {
    return this.$store.getters['auth/currentLanguageOrgName'];
  }

  get isInvalidOrientation(): boolean {
    const screenOrientation: ScreenOrientation = this.$store.state.orientation.orientation;
    return screenOrientation === ScreenOrientation.Landscape;
  }

  get isSuccess(): boolean {
    return this.viewState === ViewState.Success;
  }

  get isMessageVisible(): boolean {
    return this.messageType !== MessageType.None;
  }

  get isModalActive(): boolean {
    return this.viewState === ViewState.Error;
  }

  /** update 前に待機する場合の開始基準となる時間 */
  private delayStartTime: number = 0;

  /** update 前に待機が必要な時間 (ms) */
  private delayForUpdate: number = 0;

  private tracker!: FaceDirectionTracker;

  private directionTargets: Direction[] = [];

  private directionProgress = DirectionDetectionPhase.None;
  private frameRequest = 0;
  private isInError = false;

  public async created(): Promise<void> {
    // ページマウント時の設定
    await this.$store.dispatch('camera/mountedSetting', this.$route.path);
  }

  mounted() {
    this.tracker = this.$refs.tracker;
  }

  beforeDestroy() {
    cancelAnimationFrame(this.frameRequest);
    this.frameRequest = -1;
  }

  async update() {
    await Promise.all([setTimeoutAsync(MINIMUM_INTERVAL), this.tracker.update()]);
    if (this.tracker.currentStatus === TrackerStatus.InError || this.tracker.currentStatus === TrackerStatus.Ended) {
      return;
    }

    if (this.delayForUpdate > 0) {
      if (this.delayStartTime > 0) this.delayForUpdate = this.delayForUpdate - (Date.now() - this.delayStartTime);
      if (this.delayForUpdate > 0) await setTimeoutAsync(this.delayForUpdate);
      this.delayStartTime = 0;
      this.delayForUpdate = 0; // reset
    }

    if (this.frameRequest < 0) {
      return;
    }

    this.frameRequest = requestAnimationFrame(this.update);
  }

  private async onTrackerInitialized() {
    this.isLoading = false;
    this.messageType = MessageType.Starting;
    this.tracker.reset();
  }

  private onStart() {
    this.update(); // start update loop
  }

  private onCalibrate() {
    this.tracker.calibrate();
  }

  private onTrackerError(e: TrackerErrorEventArgs) {
    switch (e.errorType) {
      case ErrorType.FaceNotFound:
        this.errorMessageType = ErrorMessageType.FaceNotFound;
        break;

      case ErrorType.DifferentDirection:
        this.errorMessageType = ErrorMessageType.DifferentDirection;
        break;

      case ErrorType.Timeout:
        this.errorMessageType = ErrorMessageType.Timeout;
        break;

      case ErrorType.InvalidScreenOrientation:
        this.errorMessageType = ErrorMessageType.InvalidScreenOrientation;
        break;

      default:
        this.errorMessageType = ErrorMessageType.Unknown;
        break;
    }
    if (e.isManyRetry) {
      this.isManyRetry = true;
    }
    this.viewState = ViewState.Error;
  }

  private onHelpClicked() {
    this.$router.push({ name: 'face-action-introduction' });
  }

  private onRetryClicked() {
    this.guideStatusResultEffect = 'none';
    this.guideStatusRingGauge = 'none';
    this.guideProgress = 0;
    this.directionProgress = DirectionDetectionPhase.None;
    this.viewState = ViewState.None;
    this.errorMessageType = ErrorMessageType.None;
    this.tracker.reInitialized();
    this.tracker.reset();
  }

  private async onStatusChanged(status: TrackerStatus) {
    switch (status) {
      case TrackerStatus.EstablishingSession:
        await this.onTrackerInitialized();
        return;

      case TrackerStatus.Calibrating:
        this.messageType = MessageType.Calibrate;
        this.guideStatusRingGauge = 'none';
        this.guideProgress = 0;
        this.directionProgress = DirectionDetectionPhase.None;
        return;

      case TrackerStatus.TakingSelfie:
        // 自撮り撮影開始時間の記録
        this.delayStartTime = Date.now();
        return;

      case TrackerStatus.Detecting:
        this.messageType = MessageType.Wait;
        this.guideStatusRingGauge = 'calibrated';
        this.guideProgress = 0;
        this.delayForUpdate = 500;
        return;

      case TrackerStatus.InError:
        return;

      case TrackerStatus.Ended:
        // Android11でページ遷移のタイミングで固まってしまう不具合があり
        // おそらくなんらかのリソースが切迫しているので、とりあえずカメラを止めてみる
        this.tracker.preview.pauseVideo();
        this.$router.push(this.$store.getters['camera/locationNext']);
        return;
      default:
        break;
    }
  }

  private async onDirectionPhaseChanged(phase: DirectionDetectionPhase) {
    if (this.tracker.currentStatus !== TrackerStatus.Detecting) {
      return;
    }
    this.directionProgress = phase;

    switch (phase) {
      case DirectionDetectionPhase.Phase1:
      case DirectionDetectionPhase.Phase2:
      case DirectionDetectionPhase.Phase3:
        switch (this.currentDirectionTarget) {
          case Direction.Up:
            this.messageType = MessageType.DirectionUp;
            break;
          case Direction.Down:
            this.messageType = MessageType.DirectionDown;
            break;
          case Direction.Left:
            this.messageType = MessageType.DirectionLeft;
            break;
          case Direction.Right:
            this.messageType = MessageType.DirectionRight;
            break;
        }
        this.viewState = ViewState.None;
        this.delayForUpdate = 2000;
        return;

      case DirectionDetectionPhase.Center1:
      case DirectionDetectionPhase.Center2:
        this.messageType = MessageType.DirectionCenter;
        this.viewState = ViewState.Success;
        this.delayForUpdate = 2000;
        return;

      case DirectionDetectionPhase.Completed:
        this.messageType = MessageType.Complete;
        this.viewState = ViewState.Success;
        return;

      default:
        break;
    }
  }

  private async onDirectionStatusChanged(status: CheckDirectionStatus) {
    switch (status) {
      case CheckDirectionStatus.Success:
        // 「真ん中を向いてください」の際には成功表示を出さない
        if (
          this.directionProgress === DirectionDetectionPhase.Center1 ||
          this.directionProgress === DirectionDetectionPhase.Center2
        ) {
          return;
        }
        this.success();
        this.delayForUpdate = 2000;
        return;

      case CheckDirectionStatus.DifferentDirection:
      case CheckDirectionStatus.FaceNotFound:
        this.failure(status === CheckDirectionStatus.FaceNotFound);
        this.delayForUpdate = 2000;
        return;

      default:
        return;
    }
  }

  private onDirectionsChanged(value: Direction[]) {
    this.directionTargets = value;
  }

  private onPreviewPermissionDenied() {
    this.isLoading = false;
    const osType = getOSType();

    let message: string = this.$t('camera.onPermissionDenied.message') as string;
    const title1: string = this.$t('camera.onPermissionDenied.linkTitle1') as string;
    const title2: string = this.$t('camera.onPermissionDenied.linkTitle2') as string;
    const address1: string = this.$t('camera.onPermissionDenied.linkAddress1') as string;
    const address2: string = this.$t('camera.onPermissionDenied.linkAddress2') as string;
    const tag1: string = this.$t('camera.onPermissionDenied.linkTag', { title: title1, address: address1 }) as string;
    const tag2: string = this.$t('camera.onPermissionDenied.linkTag', { title: title2, address: address2 }) as string;
    if (osType === OsType.Android) {
      message = this.$t('camera.onPermissionDenied.messageAndroid', { tag1, tag2 }) as string;
    } else if (osType === OsType.iOS || osType === OsType.iPad) {
      message = this.$t('camera.onPermissionDenied.messageIos', { tag2 }) as string;
    }

    this.$buefy.dialog.alert({
      title: this.$tc('camera.onPermissionDenied.title'),
      message,
      confirmText: this.$tc('camera.onPermissionDenied.confirmText'),
      type: 'alert',
      canCancel: false,
      onConfirm: () => {
        location.reload();
      }
    });
  }

  private onCalibrateProgress(currentCount: number, maxCount: number) {
    const value = this.errorMessageType === ErrorMessageType.None ? currentCount / maxCount : 0;
    this.guideProgress = value;
  }

  private get currentDirectionTarget(): Direction {
    return this.directionTargets[this.directionProgress % this.directionTargets.length];
  }

  private success() {
    this.guideStatusResultEffect = 'success';
  }

  private failure(isNoFace: boolean) {
    this.guideStatusResultEffect = 'failure';
    this.guideStatusRingGauge = 'error';
    this.guideProgress = 0;

    this.messageType = MessageType.None;
  }

  @Watch('messageType')
  private onMessageChange(messageType: MessageType) {
    switch (messageType) {
      case MessageType.DirectionUp:
        this.guideStatusResultEffect = 'up';
        break;
      case MessageType.DirectionDown:
        this.guideStatusResultEffect = 'down';
        break;
      case MessageType.DirectionLeft:
        this.guideStatusResultEffect = 'left';
        break;
      case MessageType.DirectionRight:
        this.guideStatusResultEffect = 'right';
        break;
    }
  }
}
